/* this might become a server component in the future. It depends on ComponentRecognizer */
'use client';

import { Tooltip } from '@packages/modules/src/Tooltip/Tooltip';
import type { TooltipProps } from '@packages/modules/src/Tooltip/TooltipProps';
import { ConditionalWrapper } from '@packages/shared/src/ui/ConditionalWrapper/ConditionalWrapper';
import type { Alignment } from '../../../interfaces';
import { ComponentTypes } from '../../../interfaces';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { useGetTooltipContent } from '../../hooks/useGetTooltipContent';
import { parseBorderRadius, type BorderRadiusLonghand } from '../../utils/borderRadius';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import { classes } from '../../utils/classes';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { ImageTeaser } from '../ImageTeaser/ImageTeaser.client';
import { LinkBoxCaption } from './LinkBox.caption';
import { LinkBoxItemContent } from './LinkBox.item.content.client';
import type { LinkBoxType } from './types';
import { Decoline } from '../Decoline/Decoline';

const TooltipWrapper =
  (tooltipContent: string | null | undefined, tooltipPositioning: TooltipProps['positioning']) =>
  (ch: React.ReactNode) => (
    <Tooltip
      variant="default"
      content={tooltipContent ?? undefined}
      positioning={tooltipPositioning}
    >
      {ch}
    </Tooltip>
  );

const DecolineWrapper = () => (ch: React.ReactNode) => <div className="relative">{ch}</div>;

/**
 * Determine the needed border radius values for the image.
 */
const borderRadiusImage = (alignment: Alignment | null, borderRadius: BorderRadiusLonghand) => {
  const {
    borderTopLeftRadius,
    borderBottomLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
  } = borderRadius;
  // "alignment" specifies where the LinkBox's text is placed. Therefore the image is on the opposite side.
  switch (alignment) {
    case 'top':
      return { borderBottomLeftRadius, borderBottomRightRadius };
    case 'bottom':
      return { borderTopLeftRadius, borderTopRightRadius };
    case 'left':
      return { borderTopRightRadius, borderBottomRightRadius };
    case 'right':
      return { borderTopLeftRadius, borderBottomLeftRadius };
    default:
      return {};
  }
};

/**
 * The LinkBox component displays a teaser with maintainable texts and different layout variants
 */
export const LinkBox = ({ data }: { data: LinkBoxType }) => {
  const {
    // layout of the box
    // Options:
    // 	• Bottom (Standard, Text is below the image)
    // 	• Top (Text is above the image)
    // 	• Left (Text is to the left of the image)
    // 	• Right (Text is to the right of the image)
    behavior_paragraph_alignment: layoutAlignment = 'bottom',
    // Options:
    // 	• standard (without background color)
    // 	• boxed (with bg color)
    behavior_paragraph_variant: layoutVariant = 'standard',
    // Custom Styles
    behavior_paragraph_margin: layoutMargin,
    behavior_paragraph_borderRadius: borderRadius,
    behavior_paragraph_borderRadiusImageOnly: borderRadiusForImageOnly,
    // Cta Type
    behavior_paragraph_cta_type: ctaType,
    // Options: true, false | Note: Lazy loading should actually always be activated by default
    behavior_paragraph_lazy_load: isLazyLoading = true,
    // Background color for the boxed variant
    behavior_paragraph_background_color: backgroundColor,

    behavior_paragraph_deco_line: hasDecoline,

    // Tooltip
    behavior_paragraph_tooltip_activation: tooltipActivation,
    behavior_paragraph_tooltip_positioning: tooltipPositioning,

    // promotion item
    promotion_item: tooltipDataPromotion,
    content_snippet_bundled: tooltipDataContentSnippet,

    // link
    field_link: link,
    behavior_paragraph_link_target: linkTarget,
    behavior_paragraph_link_rel: linkRelRaw,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkSearchFilter,
    field_tracking: linkTracking,
    field_bonus_number: bonusNumber,

    // custom identifier e.G. dynamic yield
    behavior_paragraph_custom_id: customId,
    id,
  } = data;
  const linkRel = linkRelRaw === '_none' ? undefined : linkRelRaw;

  const tooltipContent = useGetTooltipContent(tooltipDataPromotion, tooltipDataContentSnippet);

  const tmpLink: string = link
    ? buildLinkWithArguments(link, {
        field_link_anchor: linkAnchor,
        field_link_highlight: linkHighlight,
        field_link_searchfilter: linkSearchFilter,
        field_tracking: linkTracking,
        field_bonus_number: bonusNumber,
      })
    : '';

  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);

  const isLandscape: boolean = layoutAlignment === 'left' || layoutAlignment === 'right';

  const parsedBorderRadius = borderRadius ? parseBorderRadius(borderRadius) : undefined;

  return (
    <div
      key={`linkBox-${id}`}
      className={classes(
        'flex h-full items-start',
        isLandscape ? 'flex-row' : 'flex-col',
        layoutVariant === 'boxed' && 'bg-color-grey-light',
      )}
      style={{
        ...(layoutVariant === 'boxed' && {
          background: backgroundColor,
        }),
        ...(layoutVariant === 'standard' && {
          background: backgroundColor || 'transparent',
        }),
        ...(!borderRadiusForImageOnly && parsedBorderRadius && parsedBorderRadius),
        margin: layoutMargin || undefined,
      }}
      data-dy={customId}
    >
      <div
        className={classes(
          'flex flex-col [&_img]:object-cover [&_img,&_a]:block [&_img,&_a]:w-full',
          (layoutAlignment === 'top' || layoutAlignment === 'left') && 'order-1',
          isLandscape ? 'h-full w-1/2 [&_img]:h-full' : 'h-auto w-full [&_img]:h-auto!',
        )}
      >
        <ConditionalWrapper
          condition={!!(tooltipContent && !!tooltipActivation)}
          wrapper={TooltipWrapper(tooltipContent, tooltipPositioning)}
        >
          <ConditionalWrapper condition={hasDecoline === true} wrapper={DecolineWrapper()}>
            <ImageTeaser
              data={data}
              link={finalLink}
              linkTarget={linkTarget}
              linkRel={linkRel}
              isLazyLoading={isLazyLoading}
              style={
                parsedBorderRadius &&
                (borderRadiusForImageOnly
                  ? parsedBorderRadius
                  : borderRadiusImage(layoutAlignment, parsedBorderRadius))
              }
            />
            {hasDecoline && <Decoline borderRadius={parsedBorderRadius} />}
          </ConditionalWrapper>
        </ConditionalWrapper>
        {ctaType === 'caption' && <LinkBoxCaption data={data} link={finalLink} />}
      </div>
      <LinkBoxItemContent
        data={{
          ...data,
          behavior_paragraph_variant: layoutVariant,
        }}
        link={finalLink}
        linkTarget={linkTarget}
        linkRel={linkRel}
        isLandscape={isLandscape}
      />
    </div>
  );
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({
  [ComponentTypes.LinkBox]: LinkBox,
});
